import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Box, Typography, Paper, Button, Card, CardContent, Grid, CircularProgress, Divider, Alert } from '@mui/material';
import { CalendarToday, Euro, ArrowBack, CreditCard, Badge, MonetizationOn } from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import { stripePaymentService } from './stripePaymentService';
import { cashPaymentService } from './cashPaymentService';
import { paypalPaymentService } from './paypalPaymentService';
import CashPaymentModal from './CashPaymentModal';
import getConfig from '../../config/app-config';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

const MembershipCheckout = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCashModal, setShowCashModal] = useState(false);
  const [membershipData, setMembershipData] = useState(null);
  // const [paypalOrderId, setPaypalOrderId] = useState(null);

  // Get configuration
  const config = getConfig();

  // Initialize membership data from location state
  useEffect(() => {
    const data = location.state?.membershipData || null;
    if (data) {
      setMembershipData(data);
    }
  }, [location.state]);

  const handleStartPayment = async () => {
    setLoading(true);
    setError('');

    const result = await stripePaymentService.createPaymentSession(membershipData.membershipListId);

    if (result.success) {
      stripePaymentService.redirectToCheckout(result.checkoutUrl);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleCashPaymentClick = () => {
    setShowCashModal(true);
  };

  const handleCashPaymentConfirm = async () => {
    setShowCashModal(false);
    setLoading(true);
    setError('');

    const result = await cashPaymentService.createCashMembership(user, { membershipListId: membershipData.membershipListId });

    if (result.success) {
      updateUser({
        ...user,
        memberships: [...user.memberships, result.membership],
      });
      navigate(`/membership-callback?payment_method=cash&payment_status=success`);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleCashPaymentCancel = () => {
    setShowCashModal(false);
  };

  const handlePayPalRedirect = async () => {
    try {
      setLoading(true);
      setError('');

      const result = await paypalPaymentService.createPayPalOrder(membershipData.membershipListId);

      if (result.success) {
        // setPaypalOrderId(result.orderId);
        // Store order ID in localStorage for redirect handling
        // localStorage.setItem('paypalOrderId', result.orderId);

        // Redirect to PayPal
        await paypalPaymentService.redirectToPayPal(result.approvalUrl);
      } else {
        console.error('PayPal order creation failed:', result.error);
        setError(`Errore nella creazione dell'ordine PayPal: ${result.error}`);
      }
    } catch (error) {
      console.error('Error in handlePayPalRedirect:', error);
      setError(`Errore nella creazione dell'ordine PayPal: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/profile');
  };

  // Mostra loader finché membershipData non è disponibile
  if (!membershipData) {
    return (
      <Container maxWidth='md'>
        <Box sx={{ py: 8, textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant='h6'>Caricamento dati tesseramento...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <CashPaymentModal open={showCashModal} onConfirm={handleCashPaymentConfirm} onCancel={handleCashPaymentCancel} />
      <Container maxWidth='md'>
        <Box sx={{ py: 4, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexShrink: 0 }}>
            <Button startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ mr: 2 }}>
              Torna al Profilo
            </Button>
            <Typography variant='h4'>{membershipData.description || 'Tesseramento'}</Typography>
          </Box>

          <Paper elevation={3} sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant='h5' gutterBottom sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Badge sx={{ mr: 1 }} />
              Dettagli Tesseramento
            </Typography>

            <Divider sx={{ my: 3, flexShrink: 0 }} />

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Grid container spacing={3} sx={{ mb: 3, flexShrink: 0 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant='outlined' sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarToday sx={{ mr: 1 }} />
                        Periodo di Validità
                      </Typography>
                      <Typography variant='body1' sx={{ mb: 1 }}>
                        <strong>Inizio:</strong> {dayjs(membershipData.startDate).locale('it').format('DD-MMM-YY')}
                      </Typography>
                      <Typography variant='body1'>
                        <strong>Fine:</strong> {dayjs(membershipData.endDate).locale('it').format('DD-MMM-YY')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant='outlined' sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <Euro sx={{ mr: 1 }} />
                        Costo
                      </Typography>
                      <Typography variant='h4' color='primary.main' sx={{ fontWeight: 'bold' }}>
                        €{membershipData.amount}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Pagamento sicuro
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1, mb: 3, flexShrink: 0 }}>
                <Typography variant='h6' gutterBottom>
                  Cosa include il tesseramento:
                </Typography>
                <Box component='ul' sx={{ pl: 2 }}>
                  <Typography component='li' variant='body2'>
                    Accesso a tutti i corsi dell'associazione
                  </Typography>
                  <Typography component='li' variant='body2'>
                    Partecipazione agli eventi e attività
                  </Typography>
                  <Typography component='li' variant='body2'>
                    Supporto tecnico e assistenza
                  </Typography>
                  {/* <Typography component='li' variant='body2'>
                    Copertura assicurativa durante le attività
                  </Typography> */}
                </Box>
              </Box>
              {error && (
                <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', flexShrink: 0, flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
                  {config.USE_STRIPE && (
                  <Button variant='contained' size='large' startIcon={<CreditCard />} onClick={handleStartPayment} disabled={loading} sx={{ px: 4, py: 1.5, width: { xs: '100%', sm: 'auto' }, minWidth: 200 }}>
                      {loading ? <CircularProgress size={24} color='inherit' /> : 'Paga con Carta'}
                    </Button>
                  )}
                  <Button variant='contained' size='large' startIcon={<MonetizationOn />} onClick={handleCashPaymentClick} disabled={loading} sx={{ px: 4, py: 1.5, width: { xs: '100%', sm: 'auto' }, minWidth: 200 }}>
                    {loading ? <CircularProgress size={24} color='inherit' /> : 'Paga in Contanti'}
                  </Button>
                  {/* Mostra PayPal solo se abilitato nella configurazione */}
                  {config.USE_PAYPAL && (
                    <Button
                      variant='contained'
                      size='large'
                      // startIcon={<PayPal />}
                      onClick={handlePayPalRedirect}
                      disabled={loading}
                      sx={{ px: 4, py: 1.5, width: { xs: '100%', sm: 'auto' }, minWidth: 200 }}
                    >
                      {loading ? <CircularProgress size={24} color='inherit' /> : 'Paga con PayPal'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default MembershipCheckout;
